// 📁 lib/scheduling/scheduleUtils.ts
// Utility functions for the scheduling system

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { db } from '../firebase';
import {
  ExpertScheduleSettings,
  Booking,
  DateOverride,
  AvailableSlot,
  WeeklySchedule,
  DayOfWeek,
  DEFAULT_SCHEDULE,
  DEFAULT_SCHEDULE_SETTINGS,
} from './types';

// ========================================
// EXPERT SCHEDULE MANAGEMENT
// ========================================

/**
 * Get or create expert's schedule settings
 */
export async function getExpertSchedule(expertId: string, createIfMissing: boolean = false): Promise<ExpertScheduleSettings> {
  try {
    const scheduleRef = doc(db, 'expertSchedules', expertId);
    const scheduleDoc = await getDoc(scheduleRef);

    if (scheduleDoc.exists()) {
      return scheduleDoc.data() as ExpertScheduleSettings;
    }

    // Return default schedule without creating if called from public page
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const defaultSchedule: ExpertScheduleSettings = {
      expertId,
      weeklySchedule: DEFAULT_SCHEDULE,
      ...DEFAULT_SCHEDULE_SETTINGS,
      timezone: userTimezone, // Auto-detect user's timezone
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as ExpertScheduleSettings;

    // Only create in Firestore if explicitly requested (when expert is logged in)
    if (createIfMissing) {
      try {
        await setDoc(scheduleRef, defaultSchedule);

        // Also update user document with timezone if not set
        try {
          const userRef = doc(db, 'users', expertId);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.data();

          if (!userData?.timezone) {
            await updateDoc(userRef, { timezone: userTimezone });
          }
        } catch (err) {
          console.warn('Could not update user timezone:', err);
        }
      } catch (writeErr) {
        console.warn('Could not create default schedule (user may not be authenticated):', writeErr);
        // Return the default schedule anyway for read-only use
      }
    }

    return defaultSchedule;
  } catch (error) {
    console.error('Error getting expert schedule:', error);
    throw error;
  }
}

/**
 * Update expert's schedule settings
 */
export async function updateExpertSchedule(
  expertId: string,
  updates: Partial<ExpertScheduleSettings>
): Promise<void> {
  try {
    const scheduleRef = doc(db, 'expertSchedules', expertId);
    // Use setDoc with merge so the first save works even when the schedule
    // doc doesn't exist yet (updateDoc throws "No document to update").
    await setDoc(scheduleRef, {
      expertId,
      ...updates,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.error('Error updating expert schedule:', error);
    throw error;
  }
}

/**
 * Add a date override (block date or custom hours)
 */
export async function addDateOverride(override: Omit<DateOverride, 'id' | 'createdAt'>): Promise<string> {
  try {
    const overrideRef = doc(collection(db, 'scheduleOverrides'));
    const newOverride: DateOverride = {
      ...override,
      id: overrideRef.id,
      createdAt: serverTimestamp(),
    } as DateOverride;

    await setDoc(overrideRef, newOverride);
    return overrideRef.id;
  } catch (error) {
    console.error('Error adding date override:', error);
    throw error;
  }
}

/**
 * Delete a date override
 */
export async function deleteDateOverride(expertId: string, date: string): Promise<void> {
  try {
    // Find the override document by expertId and date
    const overridesQuery = query(
      collection(db, 'scheduleOverrides'),
      where('expertId', '==', expertId),
      where('date', '==', date)
    );

    const snapshot = await getDocs(overridesQuery);

    // Delete all matching documents (should be only one)
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting date override:', error);
    throw error;
  }
}

/**
 * Get date overrides for an expert
 */
export async function getDateOverrides(
  expertId: string,
  startDate: Date,
  endDate: Date
): Promise<DateOverride[]> {
  try {
    const overridesQuery = query(
      collection(db, 'scheduleOverrides'),
      where('expertId', '==', expertId),
      where('date', '>=', startDate.toISOString().split('T')[0]),
      where('date', '<=', endDate.toISOString().split('T')[0])
    );

    const snapshot = await getDocs(overridesQuery);
    return snapshot.docs.map((doc) => doc.data() as DateOverride);
  } catch (error) {
    console.error('Error getting date overrides:', error);
    return [];
  }
}

// ========================================
// BOOKING MANAGEMENT
// ========================================

/**
 * Create a new booking
 */
export async function createBooking(
  bookingData: Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>,
  excludeBookingId?: string // Optional: exclude this booking ID from conflict checks (for reschedules)
): Promise<string> {
  try {
    // Check if slot is still available
    const isAvailable = await isSlotAvailable(
      bookingData.expertId,
      bookingData.scheduledStartTime.toDate(),
      bookingData.duration,
      excludeBookingId
    );

    if (!isAvailable) {
      throw new Error('This time slot is no longer available');
    }

    // Get expert email for reminders
    let expertEmail: string | undefined;
    try {
      const expertDoc = await getDoc(doc(db, 'users', bookingData.expertId));
      expertEmail = expertDoc.data()?.email;
    } catch (err) {
      console.error('Could not fetch expert email:', err);
    }

    const bookingRef = doc(collection(db, 'bookings'));
    const newBooking: Booking = {
      ...bookingData,
      id: bookingRef.id,
      status: 'pending',
      expertEmail, // Store expert email for reminder system
      reminderSent: false, // Initialize for reminder system
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as Booking;

    await setDoc(bookingRef, newBooking);

    // Send notification to expert
    await createBookingNotification(bookingData.expertId, bookingRef.id, 'booking_created');

    return bookingRef.id;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
}

/**
 * Update booking status
 */
export async function updateBookingStatus(
  bookingId: string,
  status: Booking['status'],
  userId?: string,
  reason?: string,
  meetingInfo?: { platform: string; link?: string; location?: string; instructions?: string }
): Promise<void> {
  try {
    // Get the booking BEFORE updating to ensure we have the latest data
    const booking = await getBooking(bookingId);
    if (!booking) {
      throw new Error('Booking not found');
    }

    // ✅ DUPLICATE PREVENTION: Skip if booking is already in the target status
    // This prevents duplicate emails when user clicks cancel/confirm twice or on network retries
    if (booking.status === status) {
      console.log(`⚠️ Booking ${bookingId} is already ${status}, skipping duplicate update`);
      return; // Already in target status, no action needed
    }

    const updates: any = {
      status,
      updatedAt: serverTimestamp(),
    };

    if (status === 'cancelled') {
      updates.cancelledBy = userId;
      updates.cancellationReason = reason;
    }

    if (status === 'confirmed' && meetingInfo) {
      updates.meetingPlatform = meetingInfo.platform;
      if (meetingInfo.link) {
        updates.meetingLink = meetingInfo.link;
      }
      if (meetingInfo.location) {
        updates.meetingLocation = meetingInfo.location;
      }
      if (meetingInfo.instructions) {
        updates.meetingInstructions = meetingInfo.instructions;
      }
    }

    await updateDoc(doc(db, 'bookings', bookingId), updates);

    // Merge the updates with the existing booking data for the notification
    // Only send notifications for confirmed or cancelled status changes
    // 'booking_created' is ONLY sent by createBooking, never by updateBookingStatus
    if (status !== 'confirmed' && status !== 'cancelled') {
      console.log(`⏭️ No notification needed for status: ${status}`);
      return;
    }

    const updatedBooking = {
      ...booking,
      ...updates,
      updatedAt: new Date(), // Use local date for immediate notification
    };

    const notificationType = status === 'confirmed' ? 'booking_confirmed' : 'booking_cancelled';

    // Pass the updated booking data directly to avoid race conditions
    await createBookingNotification(updatedBooking.expertId, bookingId, notificationType, updatedBooking);
  } catch (error) {
    console.error('Error updating booking status:', error);
    throw error;
  }
}

/**
 * Get a single booking
 */
export async function getBooking(bookingId: string): Promise<Booking | null> {
  try {
    const bookingDoc = await getDoc(doc(db, 'bookings', bookingId));
    if (bookingDoc.exists()) {
      return bookingDoc.data() as Booking;
    }
    return null;
  } catch (error) {
    console.error('Error getting booking:', error);
    return null;
  }
}

/**
 * Get bookings for an expert
 */
export async function getExpertBookings(
  expertId: string,
  startDate?: Date,
  endDate?: Date
): Promise<Booking[]> {
  try {
    let bookingsQuery = query(
      collection(db, 'bookings'),
      where('expertId', '==', expertId),
      orderBy('scheduledStartTime', 'asc')
    );

    if (startDate) {
      bookingsQuery = query(bookingsQuery, where('scheduledStartTime', '>=', Timestamp.fromDate(startDate)));
    }
    if (endDate) {
      bookingsQuery = query(bookingsQuery, where('scheduledStartTime', '<=', Timestamp.fromDate(endDate)));
    }

    const snapshot = await getDocs(bookingsQuery);
    return snapshot.docs.map((doc) => doc.data() as Booking);
  } catch (error) {
    console.error('Error getting expert bookings:', error);
    return [];
  }
}

/**
 * Get bookings for a quester
 */
export async function getQuesterBookings(
  questerId: string,
  startDate?: Date,
  endDate?: Date
): Promise<Booking[]> {
  try {
    let bookingsQuery = query(
      collection(db, 'bookings'),
      where('questerId', '==', questerId),
      orderBy('scheduledStartTime', 'asc')
    );

    if (startDate) {
      bookingsQuery = query(bookingsQuery, where('scheduledStartTime', '>=', Timestamp.fromDate(startDate)));
    }
    if (endDate) {
      bookingsQuery = query(bookingsQuery, where('scheduledStartTime', '<=', Timestamp.fromDate(endDate)));
    }

    const snapshot = await getDocs(bookingsQuery);
    return snapshot.docs.map((doc) => doc.data() as Booking);
  } catch (error) {
    console.error('Error getting quester bookings:', error);
    return [];
  }
}

// ========================================
// AVAILABILITY CALCULATION
// ========================================

/**
 * Get available time slots for an expert on a specific date
 */
export async function getAvailableSlots(
  expertId: string,
  date: Date,
  duration: number,
  excludeBookingId?: string // Optional: exclude this booking ID from conflict checks (for reschedules)
): Promise<AvailableSlot[]> {
  try {
    // Get expert schedule
    const schedule = await getExpertSchedule(expertId);
    const expertTimezone = schedule.timezone || 'UTC';
    const questerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Convert quester's selected date to expert's timezone to find the right day
    const dateInExpertTz = toZonedTime(date, expertTimezone);
    const dayOfWeek = getDayOfWeek(dateInExpertTz);
    const dailySchedule = schedule.weeklySchedule[dayOfWeek];

    if (!dailySchedule.enabled || dailySchedule.slots.length === 0) {
      return [];
    }

    // Check for date overrides in expert's timezone
    const dateString = dateInExpertTz.toISOString().split('T')[0];
    const overrides = await getDateOverrides(expertId, dateInExpertTz, dateInExpertTz);
    const override = overrides.find((o) => o.date === dateString);

    let slots = dailySchedule.slots;
    if (override) {
      if (override.type === 'blocked') {
        return [];
      } else if (override.type === 'custom_hours' && override.customSlots) {
        slots = override.customSlots;
      }
    }

    // Get existing bookings for the date
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const bookings = await getExpertBookings(expertId, startOfDay, endOfDay);
    // Exclude the booking being rescheduled from conflict checks
    const confirmedBookings = bookings.filter(
      (b) => (b.status === 'confirmed' || b.status === 'pending') && b.id !== excludeBookingId
    );

    // Generate available slots
    const availableSlots: AvailableSlot[] = [];
    const buffer = schedule.bookingBuffer || 15;

    for (const slot of slots) {
      const [startHour, startMin] = slot.start.split(':').map(Number);
      const [endHour, endMin] = slot.end.split(':').map(Number);

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      // ✅ OVERNIGHT SUPPORT: If end time is before start time, it spans midnight (e.g., 17:00 - 01:00)
      const isOvernightSlot = endMinutes < startMinutes;

      // Create time in expert's timezone
      let currentTimeInExpertTz = new Date(dateInExpertTz);
      currentTimeInExpertTz.setHours(startHour, startMin, 0, 0);

      const slotEndInExpertTz = new Date(dateInExpertTz);
      slotEndInExpertTz.setHours(endHour, endMin, 0, 0);

      // If overnight slot, add 1 day to end time
      if (isOvernightSlot) {
        slotEndInExpertTz.setDate(slotEndInExpertTz.getDate() + 1);
      }

      // Convert to UTC for comparison
      let currentTime = fromZonedTime(currentTimeInExpertTz, expertTimezone);
      const slotEnd = fromZonedTime(slotEndInExpertTz, expertTimezone);

      // Check min advance booking
      const now = new Date();
      const minAdvanceMs = (schedule.minAdvanceBooking || 2) * 60 * 60 * 1000;
      const earliestBooking = new Date(now.getTime() + minAdvanceMs);

      while (currentTime.getTime() + duration * 60 * 1000 <= slotEnd.getTime()) {
        const potentialEnd = new Date(currentTime.getTime() + duration * 60 * 1000);

        // Check if it's not in the past and meets min advance booking
        const isInFuture = currentTime > earliestBooking;

        // Check if it doesn't conflict with existing bookings
        // Buffer is only added AFTER a booking (expert prep/break time), not before.
        // Adding buffer on both sides causes adjacent bookings to consume the gap between them.
        const hasConflict = confirmedBookings.some((booking) => {
          const bookingStart = booking.scheduledStartTime.toDate();
          const bookingEnd = booking.scheduledEndTime.toDate();

          // Only add buffer after the booking end (expert needs break after, not before)
          const bufferedEnd = new Date(bookingEnd.getTime() + buffer * 60 * 1000);

          return (
            (currentTime >= bookingStart && currentTime < bufferedEnd) ||
            (potentialEnd > bookingStart && potentialEnd <= bufferedEnd) ||
            (currentTime <= bookingStart && potentialEnd >= bufferedEnd)
          );
        });

        if (isInFuture && !hasConflict) {
          availableSlots.push({
            start: new Date(currentTime),
            end: potentialEnd,
            duration,
            available: true,
          });
        }

        // Move to next slot (15-minute intervals)
        currentTime = new Date(currentTime.getTime() + 15 * 60 * 1000);
      }
    }

    return availableSlots;
  } catch (error) {
    console.error('Error getting available slots:', error);
    return [];
  }
}

/**
 * Check if a specific time slot is available
 */
export async function isSlotAvailable(
  expertId: string,
  startTime: Date,
  duration: number,
  excludeBookingId?: string // Optional: exclude this booking ID from conflict checks (for reschedules)
): Promise<boolean> {
  try {
    const endTime = new Date(startTime.getTime() + duration * 60 * 1000);

    // Get bookings for the day - use UTC to avoid timezone issues
    // Instead of getting just "today", get a wider range to catch all potential conflicts
    const startOfDay = new Date(startTime);
    startOfDay.setUTCHours(0, 0, 0, 0);
    const endOfDay = new Date(startTime);
    endOfDay.setUTCHours(23, 59, 59, 999);

    const bookings = await getExpertBookings(expertId, startOfDay, endOfDay);
    // Exclude the booking being rescheduled from conflict checks
    const confirmedBookings = bookings.filter(
      (b) => (b.status === 'confirmed' || b.status === 'pending') && b.id !== excludeBookingId
    );

    const schedule = await getExpertSchedule(expertId);
    const buffer = schedule.bookingBuffer || 15;

    // Check for conflicts
    // Buffer only after booking end (matches getAvailableSlots logic)
    for (const booking of confirmedBookings) {
      const bookingStart = booking.scheduledStartTime.toDate();
      const bookingEnd = booking.scheduledEndTime.toDate();

      const bufferedEnd = new Date(bookingEnd.getTime() + buffer * 60 * 1000);

      if (
        (startTime >= bookingStart && startTime < bufferedEnd) ||
        (endTime > bookingStart && endTime <= bufferedEnd) ||
        (startTime <= bookingStart && endTime >= bufferedEnd)
      ) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error checking slot availability:', error);
    return false;
  }
}

// ========================================
// NOTIFICATION HELPERS
// ========================================

async function createBookingNotification(
  userId: string,
  bookingId: string,
  type: 'booking_created' | 'booking_confirmed' | 'booking_cancelled',
  providedBooking?: Booking
): Promise<void> {
  try {
    console.log('📬 createBookingNotification called:', {
      userId: userId?.slice(-6),
      bookingId: bookingId?.slice(-6),
      type,
      source: typeof window !== 'undefined' ? 'web-client' : 'web-server',
      timestamp: new Date().toISOString(),
    });

    // Use provided booking data if available, otherwise fetch it
    const booking = providedBooking || await getBooking(bookingId);

    // Create in-app notification for expert
    const notificationId = `${userId}_${bookingId}_${type}`;
    const notificationRef = doc(db, 'notifications', notificationId);
    await setDoc(notificationRef, {
      id: notificationId,
      userId,
      bookingId,
      type,
      message: getNotificationMessage(type),
      read: false,
      createdAt: serverTimestamp(),
    });

    // ✅ Also create in-app notification for quester (if they have a valid account)
    if (booking && booking.questerId && booking.questerId !== 'guest' && booking.questerId !== userId) {
      const questerNotificationId = `${booking.questerId}_${bookingId}_${type}`;
      const questerNotificationRef = doc(db, 'notifications', questerNotificationId);
      await setDoc(questerNotificationRef, {
        id: questerNotificationId,
        userId: booking.questerId,
        bookingId,
        type,
        message: getNotificationMessage(type),
        read: false,
        createdAt: serverTimestamp(),
      });
      console.log('📬 Created in-app notification for quester:', booking.questerId?.slice(-6));
    }

    // Send email notification
    try {
      if (booking) {
        // Get expert email
        const expertDoc = await getDoc(doc(db, 'users', booking.expertId));
        const expertEmail = expertDoc.data()?.email;

        // Get quester email - handle guest users
        let questerEmail = booking.questerEmail; // Always use the email from booking first

        // If questerId is not 'guest', try to get email from user profile
        if (booking.questerId && booking.questerId !== 'guest') {
          try {
            const questerDoc = await getDoc(doc(db, 'users', booking.questerId));
            if (questerDoc.exists()) {
              questerEmail = questerDoc.data()?.email || booking.questerEmail;
            }
          } catch (err) {
            console.log('Could not fetch quester user doc, using booking email:', booking.questerEmail);
          }
        }

        console.log('🔔 Sending booking notification emails:', {
          type,
          expertEmail,
          questerEmail,
          bookingId: booking.id,
          meetingPlatform: booking.meetingPlatform,
          meetingLink: booking.meetingLink,
          meetingLocation: booking.meetingLocation
        });

        if (expertEmail || questerEmail) {
          const response = await fetch('/api/send-booking-notification-aws', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type,
              booking: {
                ...booking,
                expertSlug: booking.expertId, // Use expertId as slug for /call/[slug] URLs
                scheduledStartTime: { seconds: booking.scheduledStartTime.seconds || (booking.scheduledStartTime as any).toDate().getTime() / 1000 },
                scheduledEndTime: { seconds: booking.scheduledEndTime.seconds || (booking.scheduledEndTime as any).toDate().getTime() / 1000 },
              },
              expertEmail,
              questerEmail,
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Failed to send email notification:', errorText);
          } else {
            console.log('Email notification sent successfully');
          }
        } else {
          console.error('No email addresses found for notification');
        }

        // 📱 Send push notification to both expert and quester mobile apps
        try {
          const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://www.camyvera.com';
          const bookingPayload = {
            ...booking,
            scheduledStartTime: { seconds: booking.scheduledStartTime.seconds || (booking.scheduledStartTime as any).toDate().getTime() / 1000 },
            scheduledEndTime: { seconds: booking.scheduledEndTime.seconds || (booking.scheduledEndTime as any).toDate().getTime() / 1000 },
          };

          console.log('📱 Preparing to send push notifications for:', {
            type,
            expertId: booking.expertId,
            questerId: booking.questerId,
            questerName: booking.questerName,
            expertName: booking.expertName
          });

          // Send to expert
          console.log('📱 Sending push notification to expert:', booking.expertId?.slice(-6));
          const expertPushResponse = await fetch(`${baseUrl}/api/mobile/send-booking-push`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: booking.expertId,
              type,
              booking: bookingPayload,
            }),
          });

          if (!expertPushResponse.ok) {
            const pushErrorText = await expertPushResponse.text();
            console.error('Failed to send push notification to expert:', pushErrorText);
          } else {
            const pushResult = await expertPushResponse.json();
            console.log('📱 Push notification sent to expert:', pushResult);
          }

          // Send to quester (if they have a valid account - not 'guest')
          if (booking.questerId && booking.questerId !== 'guest') {
            console.log('📱 Sending push notification to quester:', booking.questerId.slice(-6));
            const questerPushResponse = await fetch(`${baseUrl}/api/mobile/send-booking-push`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: booking.questerId,
                type,
                booking: bookingPayload,
              }),
            });

            if (!questerPushResponse.ok) {
              const pushErrorText = await questerPushResponse.text();
              console.error('Failed to send push notification to quester:', pushErrorText);
            } else {
              const pushResult = await questerPushResponse.json();
              console.log('📱 Push notification sent to quester:', pushResult);
            }
          } else {
            console.log('⚠️ Skipping quester push notification - no valid questerId:', booking.questerId || 'undefined');
          }
        } catch (pushError) {
          console.error('Error sending push notification:', pushError);
          // Don't throw - email was sent successfully
        }
      }
    } catch (emailError) {
      console.error('Error sending email notification:', emailError);
      // Don't throw - notification created successfully
    }
  } catch (error) {
    console.error('Error creating notification:', error);
  }
}

function getNotificationMessage(type: string): string {
  switch (type) {
    case 'booking_created':
      return 'New booking request received';
    case 'booking_confirmed':
      return 'Booking confirmed';
    case 'booking_cancelled':
      return 'Booking cancelled';
    default:
      return 'Booking update';
  }
}

// ========================================
// UTILITY HELPERS
// ========================================

function getDayOfWeek(date: Date): DayOfWeek {
  const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function calculateBookingCost(rate: number, duration: number): number {
  return rate * duration;
}
